import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import SEO from "../../components/SEO";
import PageHeader from "../../components/PageHeader";
import { Reveal } from "../../components/Reveal";
import api, { formatApiError } from "../../lib/api";

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

export default function SetPasswordPage({ endpoint, title, kicker, testPrefix }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { token, password });
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title={`${title} — Espace Licencié`} />
      <PageHeader kicker={kicker} title={title} />

      <section className="py-16 sm:py-24 bg-mist">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="bg-white border border-navy/10 p-8 sm:p-10">
              {!token ? (
                <p data-testid={`${testPrefix}-no-token`} className="bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">
                  Lien incomplet. Utilisez le lien reçu par email.
                </p>
              ) : done ? (
                <div data-testid={`${testPrefix}-success`}>
                  <div className="w-14 h-14 bg-green-100 text-green-700 flex items-center justify-center mb-6">
                    <CheckCircle2 size={24} />
                  </div>
                  <p className="text-navy leading-relaxed mb-6">{done}</p>
                  <Link
                    to="/espace/connexion"
                    data-testid={`${testPrefix}-goto-login`}
                    className="inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy transition-colors duration-300"
                  >
                    Se connecter
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} data-testid={`${testPrefix}-form`}>
                  <p className="text-steel text-sm leading-relaxed mb-6">
                    Choisissez votre mot de passe : 8 caractères minimum, avec au moins une lettre et un chiffre.
                  </p>
                  <div className="mb-5">
                    <label className={labelCls} htmlFor={`${testPrefix}-password`}>Nouveau mot de passe</label>
                    <input id={`${testPrefix}-password`} data-testid={`${testPrefix}-password`} type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="new-password" />
                  </div>
                  <div className="mb-6">
                    <label className={labelCls} htmlFor={`${testPrefix}-confirm`}>Confirmer le mot de passe</label>
                    <input id={`${testPrefix}-confirm`} data-testid={`${testPrefix}-confirm`} type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} autoComplete="new-password" />
                  </div>
                  {error && <p data-testid={`${testPrefix}-error`} className="mb-6 bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">{error}</p>}
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid={`${testPrefix}-submit`}
                    className="w-full inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
                  >
                    {busy && <Loader2 size={16} className="animate-spin" />}
                    Valider mon mot de passe
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export function EspaceReinitialiser() {
  return <SetPasswordPage endpoint="/auth/reset-password" title="Réinitialiser le mot de passe" kicker="Espace Licencié" testPrefix="reset" />;
}

export function EspaceDefinirMotDePasse() {
  return <SetPasswordPage endpoint="/auth/definir-mot-de-passe" title="Définir mon mot de passe" kicker="Première connexion" testPrefix="setpass" />;
}
